/**
 * Préstamos — pantalla unificada de consulta y cancelación de préstamos.
 *
 * La vista principal es el informe general embebido (programa
 * informeprestamos: StatCards, filtro de status todos/activos/cancelados y
 * export PDF). Tarjetas de acción (diálogos con barra Volver):
 *   - "Cancelar préstamo" (programa cancelarprestamos): busca un préstamo
 *     activo y lo cancela.
 *   - "Balance de préstamos" (ares3040): activos con saldos al día.
 *   - "Préstamos saldados" (ares3050): saldados por rango de inicio.
 *   - "Cancelados por fechas" (listarcanceladosporfechas).
 *   - "Incobrables por fechas" (mismo programa): deuda castigada por fecha.
 *   - "Beneficio recuperado" (beneficiorecuperado): ganancia realizada por
 *     los pagos del período. Programa propio: expone costos y márgenes.
 *
 * Sustituye las entradas de menú de informeprestamos.js,
 * cancelacionprestamos.js, balanceprestamos.js, prestamossaldados.js y
 * listarcanceladosporfechas.js; los cinco componentes se reutilizan.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import TaskAltOutlinedIcon from "@mui/icons-material/TaskAltOutlined";
import EventBusyOutlinedIcon from "@mui/icons-material/EventBusyOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import MoneyOffOutlinedIcon from "@mui/icons-material/MoneyOffOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard, BarraVolver } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import InformePrestamo from "modules/prestamos/informeprestamos";
import CancelacionPrestamos from "modules/prestamos/cancelacionprestamos";
import BalancePrestamos from "modules/prestamos/balanceprestamos";
import PrestamosSaldados from "modules/prestamos/prestamossaldados";
import ListarCanceladoFecha from "modules/prestamos/listarcanceladosporfechas";
import IncobrablesPorFechas from "modules/prestamos/incobrablesporfechas";
import BeneficioRecuperado from "modules/prestamos/beneficiorecuperado";
import useAccionInicial from "lib/useAccionInicial";

export default function PrestamosUnificado() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa (el backend re-valida en permisos.py).
  // El informe también se muestra a quien tiene "expediente": desde el
  // listado se hace clic en un préstamo para abrir su expediente.
  const puedeInforme =
    tienePermisosModulo(roleDetalles, "informeprestamos") ||
    tienePermisosModulo(roleDetalles, "expediente");
  const puedeCancelar = tienePermisosModulo(roleDetalles, "cancelacionprestamos");
  const puedeBalance = tienePermisosModulo(roleDetalles, "ares3040");
  const puedeSaldados = tienePermisosModulo(roleDetalles, "ares3050");
  const puedeCancelados = tienePermisosModulo(roleDetalles, "listarcanceladosporfechas");
  const puedeBeneficio = tienePermisosModulo(roleDetalles, "beneficiorecuperado");

  const [cancelarOpen, setCancelarOpen] = useState(false);
  const [balanceOpen, setBalanceOpen] = useState(false);
  const [saldadosOpen, setSaldadosOpen] = useState(false);
  const [canceladosOpen, setCanceladosOpen] = useState(false);
  const [incobrablesOpen, setIncobrablesOpen] = useState(false);
  const [beneficioOpen, setBeneficioOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    cancelar: { permitido: puedeCancelar, abrir: () => setCancelarOpen(true) },
    balance: { permitido: puedeBalance, abrir: () => setBalanceOpen(true) },
    saldados: { permitido: puedeSaldados, abrir: () => setSaldadosOpen(true) },
    cancelados: { permitido: puedeCancelados, abrir: () => setCanceladosOpen(true) },
    incobrables: { permitido: puedeCancelados, abrir: () => setIncobrablesOpen(true) },
    beneficio: { permitido: puedeBeneficio, abrir: () => setBeneficioOpen(true) },
  });

  // Incrementarlo recarga el informe embebido (tras cancelar un préstamo).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Préstamos"
        subtitle="Informe general, balances, saldados y cancelación en un solo lugar"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {(puedeCancelar || puedeBalance || puedeSaldados || puedeCancelados || puedeBeneficio) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeCancelar && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={HighlightOffIcon}
                  titulo="Cancelar préstamo"
                  descripcion="Busca un préstamo activo y cancélalo"
                  color={FP.color.danger}
                  onClick={() => setCancelarOpen(true)}
                />
              </Grid>
            )}
            {puedeBalance && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={AccountBalanceOutlinedIcon}
                  titulo="Balance de préstamos"
                  descripcion="Activos con sus saldos al día"
                  color={FP.color.accentBlue}
                  onClick={() => setBalanceOpen(true)}
                />
              </Grid>
            )}
            {puedeSaldados && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={TaskAltOutlinedIcon}
                  titulo="Préstamos saldados"
                  descripcion="Saldados por rango de fecha de inicio"
                  color={FP.color.success}
                  onClick={() => setSaldadosOpen(true)}
                />
              </Grid>
            )}
            {puedeCancelados && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={EventBusyOutlinedIcon}
                  titulo="Cancelados por fechas"
                  descripcion="Préstamos cancelados en el rango"
                  color={FP.color.info}
                  onClick={() => setCanceladosOpen(true)}
                />
              </Grid>
            )}
            {puedeCancelados && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={MoneyOffOutlinedIcon}
                  titulo="Incobrables por fechas"
                  descripcion="Deuda castigada en el rango"
                  color={FP.color.danger}
                  onClick={() => setIncobrablesOpen(true)}
                />
              </Grid>
            )}
            {puedeBeneficio && (
              <Grid item xs={12} sm={6} md={3}>
                <ActionCard
                  icon={SavingsOutlinedIcon}
                  titulo="Beneficio recuperado"
                  descripcion="Ganancia realizada por los pagos del período"
                  color={FP.color.successDark}
                  onClick={() => setBeneficioOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Vista principal: informe general de préstamos */}
        {puedeInforme && <InformePrestamo embedded refreshToken={refreshKey} />}
      </Box>

      {/* Cancelar préstamo (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={cancelarOpen} onClose={() => setCancelarOpen(false)}>
        <BarraVolver onExit={() => { setCancelarOpen(false); refrescar(); }} />
        <CancelacionPrestamos />
      </Dialog>

      {/* Balance de préstamos (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={balanceOpen} onClose={() => setBalanceOpen(false)}>
        <BarraVolver onExit={() => setBalanceOpen(false)} />
        <BalancePrestamos />
      </Dialog>

      {/* Préstamos saldados (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={saldadosOpen} onClose={() => setSaldadosOpen(false)}>
        <BarraVolver onExit={() => setSaldadosOpen(false)} />
        <PrestamosSaldados />
      </Dialog>

      {/* Cancelados por fechas (pantalla existente, con barra Volver) */}
      <Dialog fullScreen open={canceladosOpen} onClose={() => setCanceladosOpen(false)}>
        <BarraVolver onExit={() => setCanceladosOpen(false)} />
        <ListarCanceladoFecha />
      </Dialog>

      {/* Incobrables por fechas (con barra Volver) */}
      <Dialog fullScreen open={incobrablesOpen} onClose={() => setIncobrablesOpen(false)}>
        <BarraVolver onExit={() => setIncobrablesOpen(false)} />
        <IncobrablesPorFechas />
      </Dialog>

      {/* Beneficio recuperado (con barra Volver) */}
      <Dialog fullScreen open={beneficioOpen} onClose={() => setBeneficioOpen(false)}>
        <BarraVolver onExit={() => setBeneficioOpen(false)} />
        <BeneficioRecuperado />
      </Dialog>
    </Box>
  );
}
